import { nectarApi } from '@/features/nectar/api/client'
import { parseMoney } from '@/utils'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'

export type PayoutMethods = {
  tremendous: {
    enabled: boolean
    paused: boolean
    // Kept as the wire string so the round-up works from exact decimal digits.
    minPayoutUsd: string | null
    maxPayoutUsd: number | null
  }
}

// Money on this endpoint: a decimal string with at least two places and no exponent.
// Checked here because `parseMoney` is `parseFloat`, which accepts `"1e1"`.
const MONEY_RE = /^\d+\.\d{2,}$/

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null

const readMoney = (v: unknown, field: string): string | null => {
  if (v === null) return null
  if (typeof v === 'string' && MONEY_RE.test(v)) return v
  throw new Error(`parsePayoutMethods: invalid ${field}: ${JSON.stringify(v)}`)
}

// Reads only `methods.tremendous`; every other key is ignored.
export const parsePayoutMethods = (raw: unknown): PayoutMethods => {
  if (!isObject(raw)) {
    throw new Error('parsePayoutMethods: body is not an object')
  }
  if (!isObject(raw.methods)) {
    throw new Error('parsePayoutMethods: methods is not an object')
  }
  const tremendous = raw.methods.tremendous
  if (!isObject(tremendous)) {
    throw new Error('parsePayoutMethods: methods.tremendous is not an object')
  }
  const minPayoutUsd = readMoney(tremendous.minPayoutUsd, 'minPayoutUsd')
  const maxWire = readMoney(tremendous.maxPayoutUsd, 'maxPayoutUsd')
  const enabled = tremendous.enabled === true
  if (enabled && minPayoutUsd === null) {
    throw new Error('parsePayoutMethods: tremendous is enabled with a null minPayoutUsd')
  }
  return {
    tremendous: {
      enabled,
      paused: tremendous.paused === true,
      minPayoutUsd,
      maxPayoutUsd: maxWire === null ? null : parseMoney(maxWire)
    }
  }
}

// Rounds a wire money string UP to whole cents using integer cents, never float addition.
export const roundUpUsdToCents = (s: string): number => {
  if (!MONEY_RE.test(s)) {
    throw new Error(`roundUpUsdToCents: invalid decimal string: ${s}`)
  }
  const [whole, frac] = s.split('.')
  let cents = Number(whole) * 100 + Number(frac.slice(0, 2))
  if (/[1-9]/.test(frac.slice(2))) cents += 1
  return cents / 100
}

export const useFetchPayoutMethods = (enabled: boolean): UseQueryResult<PayoutMethods> => {
  return useQuery({
    queryKey: ['payout-methods'],
    queryFn: async (): Promise<PayoutMethods> =>
      parsePayoutMethods(await nectarApi.get<unknown>('/v1/user/payout-methods')),
    staleTime: 30_000,
    enabled
  })
}
